import { useState, useRef, useEffect } from 'react';
import { AlertCircle, ChevronDown, X } from 'lucide-react';
import { API_BASE } from '../config';

/* ============================================================================
 * Shared helpers/components/styles between DistributorPO.jsx (create/edit
 * form) and DistributorPOHistory.jsx (the dedicated "My Recent Purchase
 * Orders" page) — same GRN-derived visual language (own --dpo-* token
 * namespace), same print view, same status/confirm patterns, so both pages
 * stay visually and behaviorally consistent without duplicating code.
 * ========================================================================== */

export const BASE = API_BASE;
export const ICON = { xs: 12, sm: 14, md: 16, lg: 20 };
export const todayISO = () => new Date().toISOString().slice(0, 10);
export const num = (v) => { const n = parseFloat(String(v ?? '').replace(/[^0-9.-]/g, '')); return isNaN(n) ? 0 : n; };
export const fmtINR = (n) => '₹' + (n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const fmtNum = (n) => (n || 0).toLocaleString('en-IN');

// Shows "Region - State" when the distributor_master row has a region
// (e.g. "North - Uttar Pradesh"); falls back to just the state name when
// there's no region (single-region distributors don't need one), and
// finally to city/'Location not set' if even state is missing.
export function formatRegionLocation(r) {
  if (r.region) return r.state ? `${r.region} - ${r.state}` : r.region;
  return r.state || r.location || 'Location not set';
}

export function statusOf(po) {
  // Cancelling from PO Tracker is the only other place status can change
  // after this PO is drafted — checked first so it overrides everything
  // else (a classified-then-cancelled PO must show Cancelled, not "Sent as X").
  if (po.status === 'cancelled') return { label: 'Cancelled', tint: 'var(--dpo-pink-tint)', color: 'var(--dpo-error)' };
  if (po.status === 'rejected') return { label: 'Rejected by distributor', tint: 'var(--dpo-pink-tint)', color: 'var(--dpo-error)' };
  if (po.order_type) return { label: `Sent as ${po.order_type}`, tint: 'var(--dpo-green-tint)', color: 'var(--dpo-green)' };
  return { label: 'Awaiting distributor approval', tint: 'var(--dpo-amber-tint)', color: 'var(--dpo-amber)' };
}

export function SectionTitle({ n, title, sub }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 18 }}>
      <span style={S.sectionBadge}>{n}</span>
      <div>
        <div style={{ fontWeight: 600, fontSize: 17, color: 'var(--dpo-text)' }}>{title}</div>
        {sub && <div style={{ fontSize: 12, color: 'var(--dpo-text-muted)' }}>{sub}</div>}
      </div>
    </div>
  );
}

// Themed dropdown matching this app's established popup-list style (same
// S.dropdown/dropdownItem visual family used by the SKU search suggestions
// and GRN's own menu popovers) — used in place of a bare native <select>
// wherever a filter needs to look consistent with the rest of the page.
export function Dropdown({ value, options, onChange, placeholder, disabled }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const btnRef = useRef(null);
  const current = options.find(o => o.value === value);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => { window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); };
  }, [open]);

  function toggle() {
    if (disabled) return;
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setRect({ top: r.bottom + 4, left: r.left, width: r.width });
    }
    setOpen(o => !o);
  }

  return (
    <>
      <button ref={btnRef} type="button" disabled={disabled}
        style={{ ...S.select, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: disabled ? 'not-allowed' : 'pointer', textAlign: 'left', opacity: disabled ? 0.6 : 1 }}
        onClick={toggle}>
        <span style={{ color: current ? 'var(--dpo-text)' : 'var(--dpo-text-muted)' }}>{current ? current.label : placeholder || 'Select…'}</span>
        <ChevronDown size={14} style={{ color: 'var(--dpo-text-muted)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
      </button>
      {open && rect && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1999 }} onClick={() => setOpen(false)}>
          <div style={{ ...S.dropdown, top: rect.top, left: rect.left, width: rect.width }} onClick={e => e.stopPropagation()}>
            {options.map(o => (
              <div key={o.value} style={{ ...S.dropdownItem, background: o.value === value ? 'var(--dpo-purple-tint)' : undefined, fontWeight: o.value === value ? 700 : 400 }}
                onClick={() => { onChange(o.value); setOpen(false); }}>
                {o.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// Shown inside a filterBox whenever any filter is active (mirrors
// GRNManagement.jsx's clearFiltersBtn — only rendered conditionally there
// too), resetting every filter passed in at once.
export function ClearFiltersButton({ onClear }) {
  return (
    <button type="button" style={S.clearFiltersBtn} onClick={onClear}>
      <X size={12} />Clear filters
    </button>
  );
}

// Every status-changing action on a PO (classify as Sales Order / Proforma
// Invoice, or Reject) routes through this confirm modal first — none of them
// are reversible once done (classifying hands the PO to Accounts; rejecting
// deletes it outright).
export function ConfirmModal({ config, onCancel, onConfirm }) {
  if (!config) return null;
  const { kind, poNumber, value } = config;
  return (
    <div style={S.overlay} onClick={onCancel}>
      <div style={{ ...S.modal, maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div style={S.warnBox}>
          <AlertCircle size={22} style={{ color: kind === 'reject' ? 'var(--dpo-error)' : 'var(--dpo-amber)', flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--dpo-text)', marginBottom: 6 }}>
              {kind === 'reject' ? 'Reject this Purchase Order?' : `Mark as ${value}?`}
            </div>
            <div style={{ fontSize: 13, color: 'var(--dpo-text-muted)', lineHeight: 1.5 }}>
              {kind === 'reject'
                ? <>This permanently deletes <b style={{ fontFamily: 'monospace' }}>{poNumber}</b> from the database. This cannot be undone.</>
                : <>This sends <b style={{ fontFamily: 'monospace' }}>{poNumber}</b> to Accounts as a {value} and can't be changed back from here. Make sure the distributor has actually confirmed this PO first.</>}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <button style={S.btnGhost} onClick={onCancel}>Cancel</button>
          <button
            style={{ ...S.btnPrimary, background: kind === 'reject' ? 'var(--dpo-error)' : '#6E58A6' }}
            onClick={onConfirm}
          >
            {kind === 'reject' ? 'Yes, Reject & Delete' : `Yes, Mark as ${value}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Sharing a PO with a distributor is now an Excel download instead of a
// print/PDF flow (the print pipeline kept fighting Chromium's pagination —
// see git history for that saga). `data` is the same {header, lines} shape
// ?action=detail returns (also hand-built for an in-progress, unsaved
// draft) — lines may or may not carry a `region_qty` object; region columns
// are derived from whatever keys are actually present, same principle as
// DistributorPerformaInvoice.jsx's dynamic region rendering.
export function exportPoToExcel(data) {
  const XLSX = window.XLSX;
  if (!XLSX) { window.alert('Excel library not loaded — check your connection and try again.'); return; }
  const h = data.header;
  const regionKeys = [...new Set(data.lines.flatMap(l => Object.keys(l.region_qty || {})))];

  const rows = [
    ['Purchase Order', h.po_number || 'DRAFT'],
    ['PO Date', h.po_date ? String(h.po_date).slice(0, 10) : ''],
    ['Distributor', h.party_name || ''],
    ['Platform', h.channel || ''],
    ['Location', h.location || ''],
    [],
    ['SKU', 'EAN', 'Case Pack', 'MRP', 'Billing Price', ...regionKeys, 'Qty', 'Amount'],
  ];
  data.lines.forEach(l => rows.push([
    l.product_name, l.ean_code, l.case_qty ?? '', num(l.mrp), num(l.billing_price),
    ...regionKeys.map(r => num(l.region_qty?.[r])),
    num(l.po_qty), num(l.po_amount),
  ]));
  rows.push([]);
  rows.push(['', '', '', '', '', ...regionKeys.map(() => ''), 'Grand Total', data.lines.reduce((s, l) => s + num(l.po_amount), 0)]);

  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Purchase Order');
  XLSX.writeFile(wb, `${h.po_number || 'Draft'}_PurchaseOrder.xlsx`);
}

/* Seeded-pseudo-random beam technique adapted from GRN's HeaderBeamBackground
 * (from animata/background/animated-beam.tsx), own class names. */
const prand = (n) => { const x = Math.sin(n * 127.1 + 311.7) * 43758.5453; return x - Math.floor(x); };
const lerp = (min, max, t) => min + (max - min) * t;
const DPO_LANES = 40;
function DpoBeam({ index }) {
  const fast = prand(index + 91) < 0.3;
  const duration = fast ? lerp(1.2, 3.2, prand(index)) : lerp(6, 14, prand(index));
  const delay = lerp(0, 9, prand(index + 13));
  const length = Math.round(lerp(10, 26, prand(index + 29)));
  const width = Math.max(2, Math.round(lerp(2, 4, prand(index + 53))));
  const opacity = fast ? lerp(0.32, 0.5, prand(index + 71)) : lerp(0.14, 0.32, prand(index + 71));
  return (
    <div className="dpo-beam" style={{
      '--duration': `${duration.toFixed(2)}s`, '--delay': `${delay.toFixed(2)}s`,
      '--length': `${length}px`, '--opacity': opacity.toFixed(2), width: `${width}px`,
    }} />
  );
}
export function DpoBeamBackground() {
  return (
    <div className="dpo-beam-bg" aria-hidden="true">
      {Array.from({ length: DPO_LANES }, (_, i) => (
        <div key={i} className="dpo-beam-lane">{prand(i + 7) < 0.3 && <DpoBeam index={i + 1} />}</div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
export const EMBEDDED_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap');

.dpo-page {
  --dpo-surface: #fff;            --dpo-surface-subtle: #FBFAFD;
  --dpo-border: #ECE7F2;          --dpo-border-soft: #E4DEEE;
  --dpo-text: #231C30;            --dpo-text-muted: #8C82A0;
  --dpo-purple: #6E58A6;          --dpo-purple-tint: #EEE9F8;
  --dpo-amber-tint: #F5EDDC; --dpo-amber: #9A7B3E;
  --dpo-green-tint: #E7F0E8; --dpo-green: #5C7E60;
  --dpo-pink-tint:  #F7E5EA; --dpo-error: #C25E77;
  --dpo-black: #000000;
}

.dpo-beam-bg { position: absolute; inset: 0; display: flex; overflow: hidden; border-radius: 16px; z-index: 0; pointer-events: none; background: #0B0A12; }
.dpo-beam-lane { position: relative; flex: 1 0 auto; width: 34px; height: 100%; }
.dpo-beam-lane::before { content: ''; position: absolute; inset: 0; left: 50%; width: 1px; background: rgba(230,222,250,0.06); transform: rotate(10deg); }
.dpo-beam {
  position: absolute; left: 50%; top: -20%; width: 2px; height: var(--length);
  transform: translateX(-50%) rotate(10deg); opacity: 0;
  background: linear-gradient(180deg, transparent, #E4DBF5, transparent);
  animation: dpo-meteor var(--duration) var(--delay) ease-in-out infinite backwards;
}
@keyframes dpo-meteor {
  0%   { top: -20%; opacity: 0; }
  12%  { opacity: var(--opacity); }
  88%  { opacity: var(--opacity); }
  100% { top: 120%; opacity: 0; }
}
@media (prefers-reduced-motion: reduce) { .dpo-beam { animation: none; opacity: 0; } }

* { scrollbar-width: thin; scrollbar-color: rgba(110,88,166,0.35) transparent; }
*::-webkit-scrollbar { width: 6px; height: 6px; }
*::-webkit-scrollbar-thumb { background: rgba(110,88,166,0.35); border-radius: 999px; }

`;

/* ---------------------------------------------------------------------- */
export const S = {
  page: { color: 'var(--dpo-text)', fontFamily: "'Space Grotesk', sans-serif" },
  headerBar: {
    position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap',
    background: '#0B0A12', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 6px 20px rgba(35,28,48,0.10)', padding: '14px 20px', marginBottom: 18, overflow: 'hidden',
  },
  h1: { fontFamily: "'Space Grotesk'", fontWeight: 300, fontSize: 22, margin: 0, color: '#F3EFFA', letterSpacing: '-0.01em' },
  backBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 9, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.06)', color: '#F3EFFA', fontWeight: 500, fontSize: 12.5, cursor: 'pointer', fontFamily: "'Space Grotesk'" },
  // Width back to 1fr/1fr (stretches to fill available space like the
  // original layout); alignItems: 'start' (not 'stretch') keeps the option
  // cards from being forced to match the taller Pending Action card's
  // height — that stretch was what made them look "very long" for how
  // little text they hold. Pending card's own height/width are untouched
  // either way (300px, already the tallest of the three).
  homeGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 300px', gap: 16, alignItems: 'start' },
  // minHeight keeps both option cards the same length regardless of
  // whether their subtitle happens to wrap to one or two lines.
  homeOptionCard: { display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left', background: 'var(--dpo-surface)', border: '1px solid var(--dpo-border)', borderRadius: 16, padding: '18px 20px', cursor: 'pointer', fontFamily: "'Space Grotesk'", boxShadow: '0 1px 2px rgba(35,28,48,0.05)', minHeight: 106, boxSizing: 'border-box' },
  homeOptionIcon: { width: 54, height: 54, borderRadius: 14, background: 'var(--dpo-purple-tint)', color: 'var(--dpo-purple)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  homeOptionTitle: { fontWeight: 700, fontSize: 18, color: 'var(--dpo-text)', marginBottom: 5 },
  homeOptionSub: { fontSize: 14, color: 'var(--dpo-text-muted)' },
  pendingCard: { display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--dpo-amber-tint)', border: '1px solid var(--dpo-border)', borderRadius: 16, padding: '22px 22px', fontFamily: "'Space Grotesk'", cursor: 'pointer', textAlign: 'left' },
  pendingCount: { fontSize: 40, fontWeight: 800, color: 'var(--dpo-amber)', lineHeight: 1 },
  editBanner: { display: 'flex', alignItems: 'center', gap: 8, background: 'var(--dpo-purple-tint)', color: 'var(--dpo-purple)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, fontWeight: 500 },
  linkBtn: { marginLeft: 'auto', border: 'none', background: 'transparent', color: 'var(--dpo-purple)', fontWeight: 700, cursor: 'pointer', fontFamily: "'Space Grotesk'", fontSize: 12.5 },
  card: { background: 'var(--dpo-surface)', borderRadius: 16, border: '1px solid var(--dpo-border)', padding: '24px 26px', marginBottom: 20, boxShadow: '0 1px 2px rgba(35,28,48,0.05)' },
  cardEditing: { border: '1px solid var(--dpo-black)', boxShadow: '0 0 0 3px rgba(0,0,0,0.10), inset 4px 0 0 var(--dpo-black), inset -4px 0 0 var(--dpo-black)' },
  warnBox: { display: 'flex', gap: 12, alignItems: 'flex-start' },
  sectionBadge: { width: 28, height: 28, borderRadius: 8, background: 'var(--dpo-purple-tint)', color: 'var(--dpo-purple)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, flexShrink: 0 },
  bentoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 },
  bentoSpan2: { gridColumn: 'span 2' },
  bentoCard: { borderRadius: 14, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--dpo-surface-subtle)', border: '1px solid var(--dpo-border)', justifyContent: 'center', minHeight: 76 },
  statLabel: { fontSize: 13, color: 'var(--dpo-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" },
  mutedNote: { fontSize: 12.5, color: 'var(--dpo-text-muted)' },
  warnNote: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--dpo-amber)' },
  select: { width: '100%', height: 38, padding: '0 10px', border: '1px solid var(--dpo-border)', borderRadius: 9, fontSize: 13, color: 'var(--dpo-text)', background: 'var(--dpo-surface)', outline: 'none', fontFamily: "'Space Grotesk'" },
  input: { width: '100%', height: 38, padding: '0 10px', border: '1px solid var(--dpo-border)', borderRadius: 9, fontSize: 13, color: 'var(--dpo-text)', background: 'var(--dpo-surface-subtle)', outline: 'none', fontFamily: "'Space Grotesk'", boxSizing: 'border-box' },
  readCell: { height: 38, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 10px', border: '1px solid var(--dpo-border)', borderRadius: 9, background: 'var(--dpo-surface-subtle)', color: 'var(--dpo-text)', fontSize: 13 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { background: 'var(--dpo-text)', color: '#fff', padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' },
  td: { padding: '8px 10px', borderBottom: '1px solid var(--dpo-border)', verticalAlign: 'top' },
  btnGhost: { display: 'flex', alignItems: 'center', gap: 7, padding: '10px 16px', borderRadius: 10, border: '1px solid var(--dpo-border-soft)', background: 'var(--dpo-surface)', color: 'var(--dpo-text)', fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: "'Space Grotesk'", whiteSpace: 'nowrap' },
  btnGreen: { display: 'flex', alignItems: 'center', gap: 7, padding: '10px 16px', borderRadius: 10, border: 'none', background: '#5C7E60', color: '#fff', fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: "'Space Grotesk'", whiteSpace: 'nowrap' },
  btnPrimary: { padding: '11px 22px', borderRadius: 10, border: 'none', background: '#6E58A6', color: '#fff', fontWeight: 500, fontSize: 13.5, cursor: 'pointer', fontFamily: "'Space Grotesk'" },
  smallBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 8, border: '1px solid var(--dpo-border-soft)', background: 'var(--dpo-surface)', color: 'var(--dpo-error)', cursor: 'pointer' },
  smallActionBtn: { display: 'flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--dpo-border-soft)', background: 'var(--dpo-surface)', color: 'var(--dpo-text)', fontWeight: 600, fontSize: 11.5, cursor: 'pointer', fontFamily: "'Space Grotesk'" },
  statusBadge: { padding: '5px 12px', borderRadius: 9, fontSize: 11.5, fontWeight: 700 },
  historyRow: { border: '1px solid var(--dpo-border)', borderRadius: 12, padding: '12px 14px', background: 'var(--dpo-surface-subtle)' },
  overlay: { position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,31,46,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal: { background: 'var(--dpo-surface)', borderRadius: 16, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', padding: '24px 28px' },
  dropdown: { position: 'fixed', zIndex: 2000, maxHeight: 260, overflow: 'auto', background: '#fff', border: '1px solid #ECE7F2', borderRadius: 10, boxShadow: '0 16px 40px rgba(43,37,54,.16)', padding: 6 },
  dropdownItem: { padding: '9px 11px', borderRadius: 8, cursor: 'pointer', fontSize: 13 },
  toast: { position: 'fixed', bottom: 24, right: 24, background: '#231C30', color: '#fff', padding: '14px 20px', borderRadius: 8, fontSize: 13, fontWeight: 500, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1200, maxWidth: 380 },
  filterBar: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 20 },
  filterField: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 160 },
  tabBar: { display: 'flex', gap: 8, marginBottom: 18 },
  tab: { padding: '10px 20px', borderRadius: 10, border: '1px solid var(--dpo-border-soft)', background: 'var(--dpo-surface)', color: 'var(--dpo-text-muted)', fontWeight: 600, fontSize: 13.5, cursor: 'pointer', fontFamily: "'Space Grotesk'" },
  tabActive: { background: 'var(--dpo-purple)', color: '#fff', border: '1px solid var(--dpo-purple)' },
  // GRN's persistent 2/3 + 1/3 split (GRNManagement.jsx: S.body/leftCol/rightCol) —
  // reused verbatim here so this page's edit-in-place panel matches that layout.
  splitBody: { display: 'flex', gap: 20, alignItems: 'flex-start' },
  splitLeftCol: { flex: '1 1 66%', minWidth: 0 },
  splitRightCol: {
    flex: '0 0 33%', minWidth: 280, maxWidth: 420,
    position: 'sticky', top: 20, alignSelf: 'flex-start',
    maxHeight: 'calc(100vh - 44px)', overflowY: 'auto',
    background: 'var(--dpo-surface)', borderRadius: 16, border: '1px solid var(--dpo-border)', boxShadow: '0 1px 2px rgba(35,28,48,0.05)',
  },
  // GRN's boxed filter bar (GRNManagement.jsx: S.filterBar), as its own
  // bordered box separate from the list/table below it.
  filterBox: {
    display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap',
    background: 'var(--dpo-surface)', border: '1px solid var(--dpo-border)', borderRadius: 14,
    boxShadow: '0 1px 2px rgba(35,28,48,0.05)', padding: '14px 16px', marginBottom: 16,
  },
  fieldRow: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10,
    background: 'var(--dpo-surface-subtle)', border: '1px solid var(--dpo-border)',
  },
  fieldRowLabel: { width: 150, flexShrink: 0, fontSize: 11, fontWeight: 600, color: 'var(--dpo-text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em' },
  // Label-on-top field boxes, two per row (span2 for full-width fields like
  // SKU Name/Remarks) — label is solid black per request, not the usual
  // muted gray statLabel color.
  fieldGrid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  fieldBoxLabel: { fontSize: 11, fontWeight: 700, color: '#000', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 2 },
  clearFiltersBtn: { display: 'flex', alignItems: 'center', gap: 5, padding: '9px 12px', borderRadius: 9, border: '1px solid var(--dpo-border-soft)', background: 'var(--dpo-purple-tint)', color: 'var(--dpo-purple)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: "'Space Grotesk'" },
};
